import React from "react";
import { format } from "date-fns";
import { IQuotationLog } from "../../models/quotationData.model";

interface QuotationLogModalProps {
  isOpen: boolean;
  onClose: () => void;
  quotationLogs: IQuotationLog[];
  loading: boolean;
}

const QuotationLogModal: React.FC<QuotationLogModalProps> = ({
  isOpen,
  onClose,
  quotationLogs: logs,
  loading,
}) => {
  if (!isOpen) return null;

  const getChangeIcon = (type: string) => {
    const iconProps = "w-4 h-4";

    switch (type) {
      case "transport":
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={iconProps}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
            />
          </svg>
        );
      case "added":
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={iconProps}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9v6a2 2 0 01-2 2H7a2 2 0 01-2-2V9a2 2 0 012-2h3V5a1 1 0 011-1h2a1 1 0 011 1v2h3a2 2 0 012 2z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 11v2m0-2h2m-2 0H10"
            />
          </svg>
        );

      case "removed":
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={iconProps}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9v6a2 2 0 01-2 2H7a2 2 0 01-2-2V9a2 2 0 012-2h10a2 2 0 012 2z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6"
            />
          </svg>
        );

      case "quantity":
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={iconProps}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
            />
          </svg>
        );
      default:
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={iconProps}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        );
    }
  };

  const getChangeDescription = (log: IQuotationLog) => {
    const changes: {
      type: string;
      description: string;
    }[] = [];
    const {
      oldTransportPrice,
      newTransportPrice,
      addedProductIds,
      removedProductIds,
      changedQuantities,
    } = log.changes;

    if (oldTransportPrice !== newTransportPrice) {
      changes.push({
        type: "transport",
        description: `Transport price changed from ₹${oldTransportPrice} to ₹${newTransportPrice}`,
      });
    }

    if (addedProductIds?.length) {
      const names = addedProductIds
        .map((p) => p?.productName || "Unknown Product")
        .join(", ");
      changes.push({
        type: "added",
        description: `Added products: ${names}`,
      });
    }

    if (removedProductIds?.length) {
      const names = removedProductIds
        .map((p) => p?.productName || "Unknown Product")
        .join(", ");
      changes.push({
        type: "removed",
        description: `Removed products: ${names}`,
      });
    }

    if (changedQuantities?.length) {
      changedQuantities.forEach((chg) => {
        const pName = chg.productId?.productName || "Unknown Product";
        changes.push({
          type: "quantity",
          description: `Quantity changed for ${pName}: ${chg.oldQuantity} → ${chg.newQuantity}`,
        });
      });
    }

    return changes.length
      ? changes
      : [
        {
          type: "none",
          description: "No changes recorded",
        },
      ];
  };

  type ChangeType = "transport" | "added" | "removed" | "quantity" | "none";

  const getChangeTypeStyles = (type: string) => {
    const styles: Record<ChangeType, string> = {
      transport: "bg-slate-100 border border-slate-300 text-slate-700",
      added: "bg-gray-100 border border-gray-300 text-gray-700",
      removed: "bg-neutral-100 border border-neutral-300 text-neutral-700",
      quantity: "bg-stone-100 border border-stone-300 text-stone-700",
      none: "bg-zinc-100 border border-zinc-300 text-zinc-600",
    };
    return styles[type as ChangeType] || styles.none;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="bg-gradient-to-r from-slate-100 to-gray-200 px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-gray-800">
                Quotation History
              </h2>
              <p className="text-blue-600 text-xs mt-1">
                Track all changes and modifications
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-700 hover:bg-gray-400 hover:bg-opacity-20 rounded-full p-2 transition-all duration-200 group"
              aria-label="Close modal"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 group-hover:rotate-90 transition-transform duration-200"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="px-8 py-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {loading ? (
            <div className="flex flex-col justify-center items-center h-64">
              <p className="text-gray-600 mt-4 font-medium">
                Loading history...
              </p>
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col justify-center items-center h-64">
              <div className="bg-gray-100 rounded-full p-6 mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-10 w-10 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                No History Available
              </h3>
              <p className="text-gray-500 text-xs text-center max-w-md">
                No changes have been made to this quotation yet. Updates and
                modifications will appear here.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <div className="flex gap-2">
                  <div className="flex space-x-2">
                    <div className="bg-blue-100 rounded-full p-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 text-blue-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                        />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800 text-xs">
                        Total Updates:
                      </h3>
                      <p className="text-xs text-gray-600">Revision history</p>
                    </div>
                  </div>
                  <div className="text-xs font-bold text-blue-600">
                    {logs.length}
                  </div>
                </div>
              </div>

              <div className="relative">
                <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-200 to-gray-200"></div>

                {logs.map((log, idx) => {
                  const changes = getChangeDescription(log);
                  const isLatest = idx === 0;

                  return (
                    <div key={idx} className="relative mb-8 last:mb-0">
                      <div
                        className={`absolute left-6 w-5 h-5 rounded-full border-4 bg-white ${isLatest
                            ? "border-blue-500 shadow-lg shadow-blue-200"
                            : "border-gray-300"
                          }`}
                      >
                        {isLatest && (
                          <div className="absolute inset-1 bg-blue-500 rounded-full animate-pulse"></div>
                        )}
                      </div>

                      <div className="ml-16">
                        <div
                          className={`bg-white border rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 ${isLatest
                              ? "border-blue-200 bg-blue-50"
                              : "border-gray-200"
                            }`}
                        >
                          <div className="px-4 pt-4 border-b border-gray-100">
                            <div className="flex justify-between items-start">
                              <div className="flex items-center space-x-3">
                                <div
                                  className={`w-7 h-7 rounded-full flex items-center justify-center text-white font-semibold text-xs ${isLatest ? "bg-blue-500" : "bg-gray-400"
                                    }`}
                                >
                                  {(log.updatedBy?.username || "System")
                                    .charAt(0)
                                    .toUpperCase()}
                                </div>
                                <div>
                                  <h4 className="font-semibold text-gray-800 text-xs">
                                    {log.updatedBy?.username || "System"}
                                  </h4>
                                  <p className="text-xs text-gray-500">
                                    {format(
                                      new Date(log.updatedAt),
                                      "dd MMM yyyy, hh:mm a"
                                    )}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                  {changes.length}{" "}
                                  {changes.length === 1 ? "change" : "changes"}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="p-4">
                            <div className="space-y-3">
                              {changes.map((change, changeIdx) => (
                                <div
                                  key={changeIdx}
                                  className={`p-3 rounded-lg ${getChangeTypeStyles(
                                    change.type
                                  )}`}
                                >
                                  <div className="flex items-start space-x-3">
                                    <div className="flex-shrink-0 mt-0.5">
                                      {getChangeIcon(change.type)}
                                    </div>
                                    <p className="text-xs font-medium leading-relaxed">
                                      {change.description}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuotationLogModal;